"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "../../atoms/link/link";
import { Text } from "../../atoms/text/text";
import { Modal, type ModalScrim } from "../../molecules/modal/modal";
import { fireErrorDisplayed } from "../../services/analytics";
import { ctaAriaLabel } from "../../services/cta-aria-label";
import type { SurfacePreset } from "../../tokens/surface-presets";
import { isAllowedOrigin } from "./iframe-origin";
import {
  DEFAULT_ALLOW,
  DEFAULT_ERROR_CONTENT,
  DEFAULT_ERROR_CTA_LABEL,
  DEFAULT_LOADING_CONTENT,
  DEFAULT_SANDBOX,
  IFRAME_LOAD_TIMEOUT_MS
} from "./iframe-modal.constants";
import styles from "./iframe-modal.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * Iframe modal — embedded content in a dialog, opened by a CTA.
 *
 * TWO ways in, and a page may use either. ONE per page in the attribute mode,
 * like legal-modal: a button or link carrying `data-iframe-modal` opens it, and
 * the trigger's `data-iframe-src` — or, for an anchor, its `href` — is the url
 * embedded. A parent may instead drive it directly with `open` + `iframe_url`.
 *
 * ⚠ A client component: in the attribute mode the url is not known until a
 * click, and the component owns the open state and the frame's load timer —
 * there is no server-renderable form of either.
 *
 * ⚠ DARK MODE HERE IS THE CHROME, NOT THE CONTENT. A cross-origin frame cannot
 * be styled from this page, so the embedded document owns its own theme; what
 * this component themes is the dialog panel, overlay and close control, which
 * the Modal supplies dark by default. CHLUP's own dark mode is WAT-12166.
 *
 * ⚠ Nothing fires an open or click event from here. The trigger's own
 * attributes drive GTM's Link Click trigger, and the Modal reports its own page
 * view from `modal_name`, or from the trigger's `data-iframe-modal-name`. Only
 * the error state fires `error_displayed`.
 *
 * Design reference and every divergence: design-conflicts/iframe-modal.mdx.
 */

/** The attribute a trigger carries to open the modal. */
export const TRIGGER_ATTRIBUTE = "data-iframe-modal";
/** Where a trigger names the url to embed, when it is not an anchor's `href`. */
export const SRC_ATTRIBUTE = "data-iframe-src";
/** Where a trigger names the modal for reporting, in place of `modal_name`. */
export const NAME_ATTRIBUTE = "data-iframe-modal-name";

const TRIGGER_SELECTOR = `[${TRIGGER_ATTRIBUTE}]`;

type FrameStatus = "loading" | "ready" | "error";

export interface IframeModalProps {
  /**
   * The dialog's accessible name. The embedded page carries its own heading, so
   * the dialog is named by this rather than a visible title. Required.
   */
  aria_label: string;
  /**
   * The `<iframe>`'s own accessible name (WCAG 4.1.2). A framed document with no
   * title is announced only as "frame". Required.
   */
  iframe_title: string;
  /**
   * The modal's reporting name — `"channel_lineup"`. Drives the Modal's page
   * view and the error link's position. A trigger carrying
   * `data-iframe-modal-name` replaces it for that opening, and the page view then
   * reports `<page URL>/<that name>`. With neither, no page view fires.
   */
  modal_name?: string;
  /**
   * CONTROLLED open state. Supply it, with `iframe_url`, to drive the modal from
   * a parent; omit it to use the `data-iframe-modal` trigger instead.
   */
  open?: boolean;
  /**
   * The embed url for the controlled path. Ignored in the attribute path, where
   * the trigger supplies it. Any http(s) page may be embedded.
   */
  iframe_url?: string;
  /** Fired after the dialog closes, however it closed — the controlled parent
   *  clears its own `open` here. */
  onClose?: () => void;
  /** Shown while the frame loads. Default is neutral, not approved copy. */
  loading_content?: string;
  /** Shown when the content is blocked, fails or times out. Default is neutral,
   *  not approved copy. */
  error_content?: string;
  /** Wording on the "open in a new tab" link offered in the error state. */
  error_cta_label?: string;
  /** The error link's accessible name, as authored; falls back to the wording
   *  when unset. */
  error_cta_aria_label?: string;
  /** The panel's glass step, passed through to the Modal. */
  surface?: SurfacePreset | (string & {});
  /** How dark the overlay behind the panel is, passed through to the Modal. */
  scrim?: ModalScrim;
}

/** The modal's reporting position — its name and nothing else, matching the
 *  Modal's own derivation. */
function modalLinkPositionFor(modalName?: string): string | undefined {
  return modalName ? `modal_${modalName}` : undefined;
}

function IframeModalImpl({
  aria_label,
  iframe_title,
  modal_name,
  open: openProp,
  iframe_url,
  onClose,
  loading_content = DEFAULT_LOADING_CONTENT,
  error_content = DEFAULT_ERROR_CONTENT,
  error_cta_label = DEFAULT_ERROR_CTA_LABEL,
  error_cta_aria_label,
  surface,
  scrim
}: IframeModalProps) {
  const isControlled = openProp !== undefined;

  const [internalOpen, setInternalOpen] = useState(false);
  const [internalUrl, setInternalUrl] = useState<string | null>(null);
  /* The opening trigger's own reporting name and destination, if it named one. */
  const [triggerReport, setTriggerReport] = useState<{ name: string; destination: string } | null>(null);

  /* Set only once a url has passed the origin guard, so the server — and a
     disallowed origin — render no `<iframe>` at all. */
  const [validatedUrl, setValidatedUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<FrameStatus>("loading");

  const open = isControlled ? Boolean(openProp) : internalOpen;
  const url = isControlled ? iframe_url ?? null : internalUrl;

  /* The last url whose frame reported `load`, so reopening an already-loaded url
     shows it at once rather than flashing the loading state again. */
  const loadedUrlRef = useRef<string | null>(null);

  /* The attribute trigger — uncontrolled only. A controlled parent owns open +
     iframe_url, and the listener would fight it. Mirrors legal-modal's listener. */
  useEffect(() => {
    if (isControlled) {
      return undefined;
    }

    function onDocumentClick(event: MouseEvent): void {
      /* Something nearer the click already handled it. */
      if (event.defaultPrevented) {
        return;
      }
      /* Leave modified and non-primary clicks to the browser — opening the
         content in a new tab is a reasonable affordance on these triggers. */
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const { target } = event;
      if (!(target instanceof Element)) {
        return;
      }

      /* `closest`, so a click on wording inside the trigger still resolves. */
      const node = target.closest(TRIGGER_SELECTOR);
      if (!node || !node.closest("[data-dds-root]")) {
        return;
      }

      /* A button names the url on `data-iframe-src`; an anchor may use its own
         `href`. No url is nothing to open — left alone so a mis-authored trigger
         stays visibly inert rather than opening an empty dialog. */
      const next = node.getAttribute(SRC_ATTRIBUTE) ?? node.getAttribute("href");
      if (!next) {
        return;
      }

      event.preventDefault();
      const name = node.getAttribute(NAME_ATTRIBUTE)?.trim();
      const { origin, pathname } = window.location;
      setTriggerReport(name ? {
        name,
        destination: `${origin}${pathname.replace(/\/+$/, "")}/${name}`
      } : null);
      setInternalUrl(next);
      setInternalOpen(true);
    }

    /* On `document`, not a wrapper: React attaches its listeners to the root
       container, so a listener there fires AFTER React's and the
       `defaultPrevented` check above is meaningful. */
    document.addEventListener("click", onDocumentClick);
    return () => document.removeEventListener("click", onDocumentClick);
  }, [isControlled]);

  /* Validate the origin and decide the frame's state. Client-side only, so the
     server never renders a frame and a disallowed origin never becomes one. */
  useEffect(() => {
    if (!open || !url) {
      return;
    }

    if (!isAllowedOrigin(url)) {
      setValidatedUrl(null);
      setStatus("error");
      return;
    }

    setValidatedUrl(url);
    /* Reopening the exact url that already loaded stays ready; anything else
       (re)loads. */
    setStatus(loadedUrlRef.current === url ? "ready" : "loading");
  }, [open, url]);

  /* The load timeout, in its OWN effect so re-arming is driven only by the frame
     identity and its state — not by an unrelated re-render, which would cancel a
     live timer through the cleanup. Armed while a frame is loading; cleared the
     moment it is ready, errored or replaced. */
  useEffect(() => {
    if (status !== "loading" || !validatedUrl) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setStatus(current => (current === "loading" ? "error" : current));
    }, IFRAME_LOAD_TIMEOUT_MS);

    return () => window.clearTimeout(timer);
  }, [status, validatedUrl]);

  /* `error_displayed` fires when the message RENDERS, not when the load fails —
     a failure the reader never sees is not reported. Latched per error so a
     re-render cannot repeat it; released when the error clears. */
  const reportedErrorRef = useRef(false);
  useEffect(() => {
    if (status === "error" && open && !reportedErrorRef.current) {
      reportedErrorRef.current = true;
      fireErrorDisplayed({
        errorMessage: error_content,
        errorCode: "iframe_unavailable"
      });
    }
    if (status !== "error") {
      reportedErrorRef.current = false;
    }
  }, [status, open, error_content]);

  const handleLoad = () => {
    loadedUrlRef.current = validatedUrl;
    setStatus(current => (current === "loading" ? "ready" : current));
  };

  const handleClose = () => {
    if (!isControlled) {
      setInternalOpen(false);
    }
    onClose?.();
  };

  const reportName = isControlled ? modal_name : triggerReport?.name ?? modal_name;
  const modalLinkPosition = modalLinkPositionFor(reportName);

  const body = (
    <div className={styles.frameWrap}>
      {status === "error" ? (
        /* Live region: the failure has to be announced, and `Text` forwards no
           unknown props, so the `aria` cannot sit on it. */
        <div className={styles.overlay} role="status">
          <Text as="p" variant="body-2">
            {error_content}
          </Text>
          {/* Offered only for an allowed origin — a refused url is not a
              destination this component sends the reader to, in a tab or
              otherwise. A timed-out first-party embed still has somewhere to go. */}
          {validatedUrl ? (
            <Link
              ariaLabel={ctaAriaLabel(error_cta_aria_label)}
              href={validatedUrl}
              newWindow
              linkDestination={validatedUrl}
              linkPosition={modalLinkPosition}
            >
              {error_cta_label}
            </Link>
          ) : null}
        </div>
      ) : (
        <>
          {validatedUrl ? (
            <iframe
              key={validatedUrl}
              allow={DEFAULT_ALLOW}
              className={styles.frame}
              onLoad={handleLoad}
              sandbox={DEFAULT_SANDBOX}
              src={validatedUrl}
              title={iframe_title}
            />
          ) : null}
          {status === "loading" ? (
            <div className={styles.overlay} role="status">
              <Text as="p" variant="body-2">
                {loading_content}
              </Text>
            </div>
          ) : null}
        </>
      )}
    </div>
  );

  return (
    <Modal
      ariaLabel={aria_label}
      modalName={reportName}
      onClose={handleClose}
      open={open}
      pageViewDestination={isControlled ? undefined : triggerReport?.destination}
      scrim={scrim}
      surface={surface}
    >
      {body}
    </Modal>
  );
}
export const IframeModal = withSafeRender(IframeModalImpl, "IframeModal");

export default IframeModal;
